import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { RAG_META } from "@/lib/rag";
import { fmtDate, todayLocal } from "@/lib/timefmt";
import { toast } from "sonner";
import { ChevronLeft, ChevronRight, Plus, Sun, Moon } from "lucide-react";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";

const DOWS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const MONTHS = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];

export default function MonthlyPage() {
  const { user, refresh } = useAuth();
  const isAdmin = (user?.role === "admin" || !!user?.admin_level) && user?.admin_mode !== false;
  const offDays = useMemo(() => new Set(user?.off_days || []), [user]);
  const nav = useNavigate();
  const today = todayLocal();

  const setOffDay = async (ds, off) => {
    try {
      await api.post("/my/off-day", { date: ds, off });
      await refresh();
      toast.success(off ? "Marked Off Duty" : "Back On Duty");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const now = new Date();
  const [view, setView] = useState({ y: now.getFullYear(), m: now.getMonth() });
  const [entries, setEntries] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [companyId, setCompanyId] = useState("");
  const [members, setMembers] = useState([]);
  const [memberId, setMemberId] = useState("");

  useEffect(() => {
    if (!isAdmin) return;
    api.get("/companies").then((r) => {
      setCompanies(r.data);
      setCompanyId((prev) => prev || (user?.company_id || r.data[0]?.id || ""));
    }).catch(() => {});
  }, [isAdmin, user]);

  useEffect(() => {
    if (!isAdmin || !companyId) return;
    api.get(`/org/members?company_id=${companyId}`).then((r) => {
      setMembers(r.data.members);
      setMemberId((prev) => prev || r.data.members[0]?.id || "");
    }).catch((e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)));
  }, [isAdmin, companyId]);

  useEffect(() => {
    if (isAdmin) {
      if (!memberId) return;
      api.get(`/org/members/${memberId}/entries`).then((r) => setEntries(r.data))
        .catch((e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)));
    } else {
      api.get("/entries").then((r) => setEntries(r.data)).catch(() => {});
    }
  }, [isAdmin, memberId]);

  const byDate = useMemo(() => {
    const m = {};
    entries.forEach((e) => { m[e.date] = e; });
    return m;
  }, [entries]);

  const shift = (d) => {
    const m = view.m + d;
    setView({ y: view.y + Math.floor(m / 12), m: ((m % 12) + 12) % 12 });
  };
  const goToday = () => { const t = new Date(); setView({ y: t.getFullYear(), m: t.getMonth() }); };

  const p = (n) => String(n).padStart(2, "0");
  const first = new Date(view.y, view.m, 1);
  const startDow = (first.getDay() + 6) % 7;
  const daysInMonth = new Date(view.y, view.m + 1, 0).getDate();
  const cells = [...Array(startDow).fill(null), ...Array.from({ length: daysInMonth }, (_, i) => i + 1)];
  const todayStr = `${now.getFullYear()}-${p(now.getMonth() + 1)}-${p(now.getDate())}`;
  const selectedMember = members.find((m) => m.id === memberId);

  const monthEntries = entries.filter((e) => {
    const d = new Date(`${e.date}T00:00:00`);
    return d.getFullYear() === view.y && d.getMonth() === view.m;
  });
  const avg = monthEntries.length ? monthEntries.reduce((s, e) => s + (e.score_total || 0), 0) / monthEntries.length : 0;
  const amberCount = monthEntries.filter((e) => e.rag === "amber").length;
  const redCount = monthEntries.filter((e) => e.rag === "red").length;
  const monthPrefix = `${view.y}-${p(view.m + 1)}-`;
  const offCount = [...offDays].filter((d) => d.startsWith(monthPrefix)).length;
  const missingCount = Array.from({ length: new Date(view.y, view.m + 1, 0).getDate() }, (_, i) => `${monthPrefix}${p(i + 1)}`)
    .filter((ds) => ds < todayStr && !byDate[ds] && (isAdmin || !offDays.has(ds))).length;

  return (
    <div>
      <div className="flex items-start justify-between gap-4 flex-wrap mb-6">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-slate-400 font-mono-num">Monthly View</div>
          <h1 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight mt-1">
            {MONTHS[view.m]} {view.y}
          </h1>
          <p className="text-slate-400 text-sm mt-1">
            {isAdmin ? (selectedMember ? `${selectedMember.first_name} ${selectedMember.last_name}` : "Select a member") : "Your daily scores"}
          </p>
        </div>
      </div>

      <div className="flex flex-wrap gap-3 mb-5">
        <div className="px-4 py-2.5 rounded-lg border border-slate-800 bg-slate-900/60" data-testid="monthly-stat-count">
          <div className="text-[10px] uppercase tracking-widest text-slate-400 font-mono-num">Entries</div>
          <div className="text-xl font-mono-num">{monthEntries.length}</div>
        </div>
        <div className="px-4 py-2.5 rounded-lg border border-slate-800 bg-slate-900/60" data-testid="monthly-stat-avg">
          <div className="text-[10px] uppercase tracking-widest text-slate-400 font-mono-num">Avg score</div>
          <div className="text-xl font-mono-num">{avg.toFixed(1)}</div>
        </div>
        <div className="px-4 py-2.5 rounded-lg border border-slate-800 bg-slate-900/60" data-testid="monthly-stat-amber">
          <div className="text-[10px] uppercase tracking-widest text-amber-400 font-mono-num">Amber days</div>
          <div className="text-xl font-mono-num">{amberCount}</div>
        </div>
        <div className="px-4 py-2.5 rounded-lg border border-slate-800 bg-slate-900/60" data-testid="monthly-stat-red">
          <div className="text-[10px] uppercase tracking-widest text-red-400 font-mono-num">Red days</div>
          <div className="text-xl font-mono-num font-semibold text-red-400">{redCount}</div>
        </div>
        <div className="px-4 py-2.5 rounded-lg border border-slate-800 bg-slate-900/60" data-testid="monthly-stat-off">
          <div className="text-[10px] uppercase tracking-widest text-slate-400 font-mono-num">Off duty</div>
          <div className="text-xl font-mono-num">{offCount}</div>
        </div>
        <div className="px-4 py-2.5 rounded-lg border border-slate-800 bg-slate-900/60" data-testid="monthly-stat-missing">
          <div className="text-[10px] uppercase tracking-widest text-slate-400 font-mono-num">Missing</div>
          <div className="text-xl font-mono-num">{missingCount}</div>
        </div>
        <div className="flex items-center gap-2 ml-auto self-center">
          {isAdmin && (
            <>
              {((user?.role === "admin" && !user?.admin_level) || user?.admin_level === "global") && companies.length > 1 && (
                <select value={companyId} onChange={(e) => { setCompanyId(e.target.value); setMemberId(""); }}
                  data-testid="monthly-company-select"
                  className="px-3 py-2 rounded-md bg-slate-900 border border-slate-800 text-sm">
                  {companies.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              )}
              <select value={memberId} onChange={(e) => setMemberId(e.target.value)} data-testid="monthly-member-select"
                className="px-3 py-2 rounded-md bg-slate-900 border border-slate-800 text-sm max-w-56">
                {members.length === 0 && <option value="">No members</option>}
                {members.map((m) => <option key={m.id} value={m.id}>{m.position ? `${m.position} ` : ""}{m.first_name} {m.last_name}</option>)}
              </select>
            </>
          )}
          <button onClick={() => shift(-1)} data-testid="monthly-prev"
            className="h-9 w-9 grid place-items-center rounded-md bg-slate-900 border border-slate-800 hover:border-slate-600 transition-colors"
            aria-label="Previous month"><ChevronLeft className="h-4 w-4" /></button>
          <button onClick={goToday} data-testid="monthly-today-btn"
            className="px-3 h-9 rounded-md bg-slate-900 border border-slate-800 hover:border-slate-600 text-sm transition-colors">This month</button>
          <button onClick={() => shift(1)} data-testid="monthly-next"
            className="h-9 w-9 grid place-items-center rounded-md bg-slate-900 border border-slate-800 hover:border-slate-600 transition-colors"
            aria-label="Next month"><ChevronRight className="h-4 w-4" /></button>
        </div>
      </div>

      <div className="rounded-lg border border-slate-800 bg-slate-900/60 p-4" data-testid="monthly-calendar">
        {!isAdmin && (
          <p className="text-[11px] text-slate-400 mb-3">Click a day for actions — or double-click a day marked <span className="text-slate-200">Missing entry</span> to add one.</p>
        )}
        <div className="grid grid-cols-7 gap-1.5">
          {DOWS.map((d) => (
            <div key={d} className="text-center text-[10px] uppercase tracking-widest text-slate-400 font-mono-num py-1">{d}</div>
          ))}
          {cells.map((day, i) => {
            if (!day) return <div key={`x${i}`} className="min-h-16" />;
            const ds = `${view.y}-${p(view.m + 1)}-${p(day)}`;
            const e = byDate[ds];
            const meta = e ? RAG_META[e.rag] : null;
            const isOff = !isAdmin && offDays.has(ds);
            const isMissing = !e && !isOff && ds < todayStr;
            const canAdd = !e && !isOff && ds <= todayStr;
            const cell = (
              <div data-testid={`monthly-cell-${ds}`}
                onDoubleClick={!isAdmin && canAdd ? () => nav(`/fatigue?date=${ds}`) : undefined}
                title={isOff ? `${fmtDate(ds)} — Off Duty` : e ? `${fmtDate(e.date)} — score ${Number(e.score_total).toFixed(1)} (${e.rag})` : isMissing ? `${fmtDate(ds)} — Missing entry (double-click to add)` : fmtDate(ds)}
                className={`min-h-16 rounded-md border p-1.5 transition-colors outline-none ${
                  isOff ? "border-dashed border-slate-400/70 bg-slate-400/20"
                  : e ? `${meta.cls}` : isMissing ? "border-dashed border-slate-700 bg-slate-950/30" : "border-slate-800/60 bg-slate-950/30"
                } ${ds === todayStr ? "ring-1 ring-blue-500/60" : ""} ${!isAdmin ? "cursor-pointer hover:border-slate-600" : ""}`}>
                <div className="text-[10px] font-mono-num text-slate-300">{day}</div>
                {isOff && <div className="mt-1 text-[9px] uppercase tracking-widest font-mono-num text-slate-200" data-testid={`off-day-${ds}`}>Off Duty</div>}
                {isMissing && <div className="mt-1 text-[9px] uppercase tracking-widest font-mono-num text-slate-500" data-testid={`missing-day-${ds}`}>Missing entry</div>}
                {!isOff && e && (
                  <div className="mt-1">
                    <div className="font-mono-num text-sm font-semibold">{Number(e.score_total).toFixed(1)}</div>
                    <div className="text-[9px] uppercase tracking-widest font-mono-num opacity-80">{meta.label}</div>
                  </div>
                )}
              </div>
            );
            if (isAdmin) return <div key={ds}>{cell}</div>;
            return (
              <DropdownMenu key={ds} modal={false}>
                <DropdownMenuTrigger asChild>{cell}</DropdownMenuTrigger>
                <DropdownMenuContent align="start" data-testid={`day-menu-${ds}`}
                  className="bg-slate-900 border-slate-700 text-slate-100 min-w-40">
                  <DropdownMenuItem
                    disabled={!!e || ds > today}
                    onClick={() => nav(`/fatigue?date=${ds}`)}
                    data-testid={`day-menu-add-${ds}`}
                    className="gap-2 data-[disabled]:opacity-40 data-[disabled]:pointer-events-none">
                    <Plus className="h-3.5 w-3.5" /> Add Entry
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    disabled={!isOff}
                    onClick={() => setOffDay(ds, false)}
                    data-testid={`day-menu-on-${ds}`}
                    className="gap-2 data-[disabled]:opacity-40 data-[disabled]:pointer-events-none">
                    <Sun className="h-3.5 w-3.5" /> On Duty
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    disabled={isOff}
                    onClick={() => setOffDay(ds, true)}
                    data-testid={`day-menu-off-${ds}`}
                    className="gap-2 data-[disabled]:opacity-40 data-[disabled]:pointer-events-none">
                    <Moon className="h-3.5 w-3.5" /> Off Duty
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            );
          })}
        </div>
      </div>
    </div>
  );
}
